import { toast } from "@/components/ui/sonner";
import { createRegistrantCheckPayment } from "@/hera-api/client";
import {
  createLicenseMutation,
  createPendingPaymentRecordsMutation,
  makeLicensePaymentMutation,
  renewLicenseMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { client } from "@/hera-api/client/client.gen";
import type {
  AdditionalQuestionAnswer,
  CreateLicenseRequestSchema,
  JsonValueType,
  LicensePeriodCreationParams,
  RenewLicenseRequestSchema,
} from "@/hera-api/client/types.gen";
import { getQueryErrorMessage } from "@/lib/utils";
import type { LicenseAnswers } from "@/types/license";
import { useMutation } from "@tanstack/react-query";

interface UseLicenseFormMutationsOptions {
  onSuccess?: (licenseId: string) => void;
  onError?: (error: unknown) => void;
}

export function useLicenseFormMutations(
  options: UseLicenseFormMutationsOptions = {},
) {
  const createLicenseMutationHook = useMutation({
    ...createLicenseMutation(),
  });

  const renewLicenseMutationHook = useMutation({
    ...renewLicenseMutation(),
  });

  const createPendingPaymentsMutationHook = useMutation({
    ...createPendingPaymentRecordsMutation(),
  });

  const makePaymentMutationHook = useMutation({
    ...makeLicensePaymentMutation(),
  });

  const createCheckPaymentMutationHook = useMutation({
    mutationFn: async ({
      licenseId,
      periods,
      inspectionFee,
    }: {
      licenseId: string;
      periods: LicensePeriodCreationParams[];
      inspectionFee?: string;
    }) => {
      const response = await createRegistrantCheckPayment({
        path: { license_id: licenseId },
        body: {
          periods,
          inspection_fee: inspectionFee
            ? Number.parseFloat(inspectionFee)
            : undefined,
        },
      });
      if (response.error || !response.data)
        throw new Error("Failed to create check payment");
      return {
        check_id: response.data.check_id,
        check_mailing_address: response.data.check_mailing_address ?? null,
      };
    },
  });

  const createInvoicePaymentMutationHook = useMutation({
    mutationFn: async ({
      licenseId,
      periods,
      inspectionFee,
    }: {
      licenseId: string;
      periods: LicensePeriodCreationParams[];
      inspectionFee?: string;
    }) => {
      const result = await client.post({
        url: `/registrant/licenses/${licenseId}/invoice-payment`,
        body: {
          periods,
          inspection_fee: inspectionFee
            ? Number.parseFloat(inspectionFee)
            : undefined,
        },
        parseAs: "json",
        security: [{ scheme: "bearer", type: "http" }],
      });
      type InvoicePaymentCreated = {
        check_id: string;
        check_mailing_address: string | null;
      };
      const data = result.data as InvoicePaymentCreated | undefined;
      if (result.error || !data) {
        throw new Error("Failed to create invoice payment");
      }
      return {
        check_id: data.check_id,
        check_mailing_address: data.check_mailing_address ?? null,
      };
    },
  });

  const handleCreateLicense = async (
    licenseData: CreateLicenseRequestSchema,
  ) => {
    try {
      const license = await createLicenseMutationHook.mutateAsync({
        body: licenseData,
      });

      options.onSuccess?.(license.license_id);
      return license;
    } catch (error) {
      const errorMessage = `Failed to create license: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const handleRenewLicense = async (
    licenseData: {
      license_id: string;
    } & RenewLicenseRequestSchema,
  ) => {
    try {
      await renewLicenseMutationHook.mutateAsync({
        body: licenseData,
        path: {
          license_id: licenseData.license_id,
        },
      });

      options.onSuccess?.(licenseData.license_id);
    } catch (error) {
      const errorMessage = `Failed to renew license: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const handleCreatePendingPayments = async (
    licenseId: string,
    periods: LicensePeriodCreationParams[],
    inspectionFee?: string,
    inspectionStripeFee?: string,
    bulkPaymentPayLaterSelected?: boolean | null,
  ) => {
    try {
      await createPendingPaymentsMutationHook.mutateAsync({
        path: {
          license_id: licenseId,
        },
        body: {
          periods,
          inspection_fee: inspectionFee
            ? Number.parseFloat(inspectionFee)
            : undefined,
          inspection_stripe_fee: inspectionStripeFee
            ? Number.parseFloat(inspectionStripeFee)
            : undefined,
          bulk_payment_pay_later_selected: bulkPaymentPayLaterSelected,
        },
      });
    } catch (error) {
      const errorMessage = `Failed to create pending payment records: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const handleCreateCheckPayment = async (
    licenseId: string,
    periods: LicensePeriodCreationParams[],
    inspectionFee?: string,
  ) => {
    try {
      const result = await createCheckPaymentMutationHook.mutateAsync({
        licenseId,
        periods,
        inspectionFee,
      });
      return result;
    } catch (error) {
      const errorMessage = `Failed to create check payment: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const handleCreateInvoicePayment = async (
    licenseId: string,
    periods: LicensePeriodCreationParams[],
    inspectionFee?: string,
  ) => {
    try {
      return await createInvoicePaymentMutationHook.mutateAsync({
        licenseId,
        periods,
        inspectionFee,
      });
    } catch (error) {
      const errorMessage = `Failed to create invoice payment: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const handleMakePayment = async (
    licenseId: string,
    paymentMethodType?: "card" | "us_bank_account",
  ) => {
    try {
      const session = await makePaymentMutationHook.mutateAsync({
        path: {
          license_id: licenseId,
        },
        body: {
          payment_success_url: `${window.location.origin}/registrant/payment-success?sessionId={CHECKOUT_SESSION_ID}`,
          payment_cancel_url: `${window.location.origin}/registrant/payment-cancel?sessionId={CHECKOUT_SESSION_ID}`,
          payment_method_type: paymentMethodType,
        },
      });

      window.location.href = session.checkout_session_url;
    } catch (error) {
      const errorMessage = `Failed to create payment session: ${getQueryErrorMessage(error)}`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      options.onError?.(error);
      throw error;
    }
  };

  const processLicenseAnswers = (
    licenseAnswers: LicenseAnswers,
  ): AdditionalQuestionAnswer[] => {
    const answers: AdditionalQuestionAnswer[] = [];
    for (const [key, value] of Object.entries(licenseAnswers)) {
      if (key !== "effective_date") {
        answers.push({
          question_id: key,
          answer: value as JsonValueType,
        });
      }
    }
    return answers;
  };

  return {
    handleCreateLicense,
    handleRenewLicense,
    handleCreatePendingPayments,
    handleCreateCheckPayment,
    handleCreateInvoicePayment,
    handleMakePayment,
    processLicenseAnswers,
    isCreating: createLicenseMutationHook.isPending,
    isRenewing: renewLicenseMutationHook.isPending,
    isCreatingPendingPayments: createPendingPaymentsMutationHook.isPending,
    isMakingPayment: makePaymentMutationHook.isPending,
    isCreatingCheckPayment: createCheckPaymentMutationHook.isPending,
    isCreatingInvoicePayment: createInvoicePaymentMutationHook.isPending,
  };
}
